import React, { useState, useEffect } from 'react';
import { Paper, Typography, Button, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';

import PIIValidationDropzone from './PIIValidationDropzone';
import PIIValidationFileList from './PIIValidationFileList';
import ValidationReportModal from './ValidationReportModal';
import ValidationResultModal from './ValidationResultModal';
import ValidationScopeSelector from './ValidationScopeSelector';

// API Services
import {
  validateFilePII,
  anonymizeFilePII,
  pseudoAnonymizeDocxPII,
} from '../../../services/piiService';
import { startAsyncAnonymization } from '../../../services/asyncAnonymizationService';
import { createBatchId } from '../../../shared/createBatchId';

const ACCEPTED_EXTENSIONS = ['.pdf', '.docx'];
const randomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const PIIValidationUploadPanel = () => {
  const { t } = useTranslation();

  const [files, setFiles] = useState([]);

  // Rozsah kontroly — GDPR je implicitné, NIS2 a VYHRADENÉ si volí používateľ.
  const [scope, setScope] = useState({ nis2: false, classified: false });
  const [isUploading, setIsUploading] = useState(false);
  const [globalError, setGlobalError] = useState(null);

  // Modal state – držíme celý file objekt (kvôli reportu aj akciám nad súborom)
  const [activeFileObj, setActiveFileObj] = useState(null);

  // Nahlad vysledneho (anonymizovaneho / pseudonymizovaneho) suboru - { blob, filename }
  const [resultPreview, setResultPreview] = useState(null);

  // Fake upload progress
  useEffect(() => {
    const hasUploading = files.some(f => f.status === 'uploading');
    if (!hasUploading) return;

    const interval = setInterval(() => {
      setFiles(prev => prev.map(f => {
        if (f.status !== 'uploading' || f.progress >= f.plateau) return f;
        return { ...f, progress: Math.min(f.plateau, f.progress + f.speed) };
      }));
    }, 400);

    return () => clearInterval(interval);
  }, [files]);

  const handleFilesAdded = (fileList) => {
    setGlobalError(null);

    const newFiles = Array.from(fileList || []);
    const validFiles = [];
    const invalidFiles = [];

    newFiles.forEach((file) => {
      const isAccepted = ACCEPTED_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext));
      if (isAccepted) {
        validFiles.push({
          id: `${file.name}_${file.size}_${Date.now()}`,
          file,
          name: file.name,
          size: file.size,
          status: 'pending',
          progress: 0,
          error: null,
          report: null,
          plateau: randomInt(85, 95),
          speed: randomInt(2, 5),
        });
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (validFiles.length > 0) setFiles(prev => [...prev, ...validFiles]);
    if (invalidFiles.length > 0) setGlobalError(`Nepodporované formáty: ${invalidFiles.join(', ')}`);
  };

  const removeFile = (id) => {
    if (isUploading) return;
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const validateAll = async () => {
    const pendingFiles = files.filter(f => f.status === 'pending' || f.status === 'error');
    if (!pendingFiles.length) return;

    setIsUploading(true);
    setGlobalError(null);

    const batchId = createBatchId();

    // Rozsah kontroly ide do backendu ako options — nezvolené okruhy sa vôbec
    // nedetegujú (neposielajú sa do Cenzor API).
    const scopeOptions = {
      validate_nis2: !!scope.nis2,
      validate_classified: !!scope.classified,
    };

    await Promise.all(pendingFiles.map(async (fileObj) => {
      const { id, file } = fileObj;
      const isPdf = file.name.toLowerCase().endsWith('.pdf');

      setFiles(prev => prev.map(f => f.id === id ? { ...f, status: 'uploading', error: null, progress: 5 } : f));

      try {
        if (isPdf) {
          await startAsyncAnonymization(file, { ...scopeOptions }, batchId);
          setFiles(prev => prev.map(f => f.id === id ? { ...f, status: 'queued', progress: 100 } : f));
        } else {
          const res = await validateFilePII(file, { anonymizationGroupId: batchId, ...scopeOptions });
          const report = res?.data ? res.data : res;
          setFiles(prev => prev.map(f => f.id === id ? { ...f, status: 'success', progress: 100, report } : f));
        }
      } catch (err) {
        setFiles(prev => prev.map(f => f.id === id ? { ...f, status: 'error', progress: 100, error: err.message } : f));
      }
    }));

    setIsUploading(false);
  };

  // Akcie z reportu kontroly redigujú všetky skupiny, ktoré report označil —
  // teda GDPR + tie okruhy, ktoré si používateľ zvolil v rozsahu kontroly.
  // Inak by ostali sieťové/utajované údaje čitateľné napriek tomu, že kontrola
  // ich vyhodnotila ako NEPREŠLO; a naopak neredigujeme nič, čo sa nekontrolovalo.
  const REPORT_REDACT_ALL = {
    anonymize_nis2: !!scope.nis2,
    anonymize_classified: !!scope.classified,
  };

  // Priame spustenie anonymizácie nad konkrétnym súborom z reportu
  const handleAnonymize = async (fileObj) => {
    if (!fileObj?.file) return { success: false, error: 'Súbor nie je dostupný.' };
    const res = await anonymizeFilePII(fileObj.file, { ...REPORT_REDACT_ALL });
    if (res?.success && res.data?.blob) {
      setResultPreview({ blob: res.data.blob, filename: res.data.filename || `anonymized_${fileObj.name}` });
      setActiveFileObj(null);
    }
    return res;
  };

  // Priame spustenie pseudonymizácie (len DOCX)
  const handlePseudonymize = async (fileObj) => {
    if (!fileObj?.file) return { success: false, error: 'Súbor nie je dostupný.' };
    const res = await pseudoAnonymizeDocxPII(fileObj.file, { ...REPORT_REDACT_ALL });
    if (res?.success && res.data?.blob) {
      setResultPreview({ blob: res.data.blob, filename: res.data.filename || `pseudonymized_${fileObj.name}` });
      setActiveFileObj(null);
    }
    return res;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        height: '100%'
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        {t('PIIValidationUploadPanel.title', 'Kontrola súborov')}
      </Typography>

      <ValidationScopeSelector scope={scope} onChange={setScope} disabled={isUploading} />

      <PIIValidationDropzone disabled={isUploading} onFilesAdded={handleFilesAdded} />

      <PIIValidationFileList
        files={files}
        isUploading={isUploading}
        onRemove={removeFile}
        onViewReport={(fileObj) => setActiveFileObj(fileObj)}
      />

      {globalError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {globalError}
        </Alert>
      )}

      <Button
        variant="contained"
        color="primary"
        fullWidth
        disableElevation
        disabled={isUploading || files.length === 0}
        onClick={validateAll}
        size="large"
        sx={{ borderRadius: 2, fontWeight: 600 }}
      >
        {isUploading
          ? t('PIIValidationUploadPanel.processing', 'Prebieha kontrola...')
          : t('PIIValidationUploadPanel.start', 'Spustiť kontrolu údajov')}
      </Button>

      <ValidationReportModal
        open={!!activeFileObj}
        report={activeFileObj?.report}
        fileObj={activeFileObj}
        onClose={() => setActiveFileObj(null)}
        onAnonymize={handleAnonymize}
        onPseudonymize={handlePseudonymize}
      />

      <ValidationResultModal
        open={!!resultPreview}
        result={resultPreview}
        onClose={() => setResultPreview(null)}
      />
    </Paper>
  );
};

export default PIIValidationUploadPanel;
